import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import {
    useDownloadVisitaActaMutation,
    useLazyGetLogAuditoriaVisitaQuery
} from '../../../services/visitasApi';
import { CommonMimeType, downloadBase64File } from '../../../utils/base64FileDownload';
import { FallaVisita, Visita } from '../consts/visitasTypes';
import {
    fallasDeLaVisita,
    fallasDesdeLogAuditoria,
    fechaDeFalla,
    idDeLaVisita,
    nombreArchivoActaFallida,
    pedidoActaDeFalla
} from '../consts/actaFallida';

// Acta de visita fallida: cada falla de la visita tiene la suya y se bajan
// desde el drawer de descarga, eligiendo una o varias (SMP-599). Las fallas
// salen de la grilla; si no vienen (visitas fallidas anteriores al registro del
// evento de auditoría en el GET de la grilla) se piden al historial.
const useActaFallida = () => {
    const { t } = useTranslation('visitas');
    const [visitaParaElegir, setVisitaParaElegir] = useState<any>(null);
    const [fallasParaElegir, setFallasParaElegir] = useState<FallaVisita[] | null>(null);
    const [descargandoActas, setDescargandoActas] = useState(false);
    const [getLogAuditoriaVisita] = useLazyGetLogAuditoriaVisitaQuery();
    const [downloadVisitaActa] = useDownloadVisitaActaMutation();

    const abrirDrawer = (visita: any, fallas: FallaVisita[]) => {
        if (fallas.length === 0) {
            enqueueSnackbar(t('alerts.downloadActaFallida.sinFallas'), { variant: 'error' });
            return;
        }

        setVisitaParaElegir(visita);
        setFallasParaElegir(fallas);
    };

    const descargarActaFallida = async (visita: Visita | null) => {
        if (!visita) return;

        const fallasDeLaGrilla = fallasDeLaVisita(visita);
        if (fallasDeLaGrilla.length > 0) {
            abrirDrawer(visita, fallasDeLaGrilla);
            return;
        }

        try {
            // Mismo id que usa el pedido del acta: el evento tiene que ser de
            // esa visita.
            const response = await getLogAuditoriaVisita({
                idVisita: idDeLaVisita(visita)
            }).unwrap();

            abrirDrawer(visita, fallasDesdeLogAuditoria(response?.data));
        } catch (error) {
            enqueueSnackbar(t('alerts.downloadActaFallida.error'), { variant: 'error' });
        }
    };

    const cerrarEleccionDeFalla = () => {
        setVisitaParaElegir(null);
        setFallasParaElegir(null);
    };

    // De a un acta por vez: cada una se genera en el backend y en serie no se
    // pisan las descargas del navegador. Un solo aviso al final con el resultado.
    const descargarActasDeFallas = async (idsEvento: number[]) => {
        if (descargandoActas || !visitaParaElegir) return;

        const fallas = idsEvento
            .map((idEvento) => fallasParaElegir?.find((falla) => falla.idEvento === idEvento))
            .filter((falla): falla is FallaVisita => Boolean(falla));
        if (fallas.length === 0) return;

        setDescargandoActas(true);
        const fallidas: FallaVisita[] = [];
        const prefijo = String(t('table.actions.downloadActaFallidaFilename'));

        for (const falla of fallas) {
            try {
                const response = await downloadVisitaActa(
                    pedidoActaDeFalla(visitaParaElegir, falla.idEvento)
                ).unwrap();

                if (!response?.data) throw new Error('Respuesta sin PDF');

                downloadBase64File({
                    base64Data: response.data,
                    fileName: nombreArchivoActaFallida(falla, prefijo),
                    mimeType: CommonMimeType.PDF
                });
            } catch (error) {
                fallidas.push(falla);
            }
        }

        setDescargandoActas(false);
        cerrarEleccionDeFalla();

        if (fallidas.length === 0) {
            enqueueSnackbar(
                fallas.length === 1
                    ? t('alerts.downloadActaFallida.success')
                    : t('alerts.downloadActaFallida.successVarias', { count: fallas.length }),
                { variant: 'success' }
            );
            return;
        }

        enqueueSnackbar(
            t(
                fallidas.length === 1
                    ? 'alerts.downloadActaFallida.errorActa'
                    : 'alerts.downloadActaFallida.errorActas',
                { fechas: fallidas.map(fechaDeFalla).join(', ') }
            ),
            { variant: 'error' }
        );
    };

    return {
        visitaParaElegir,
        fallasParaElegir,
        descargandoActas,
        descargarActaFallida,
        descargarActasDeFallas,
        cerrarEleccionDeFalla
    };
};

export default useActaFallida;
